import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { matchesService } from "@/api/services/matches.service";
import type {
  Match,
  CreateMatchRequest,
  UpdateMatchRequest,
  MatchFilters,
  MatchStatus,
} from "@/types/api.types";

export const matchKeys = {
  all: ["matches"] as const,
  lists: () => [...matchKeys.all, "list"] as const,
  list: (filters?: MatchFilters) => [...matchKeys.lists(), filters] as const,
  detail: (id: number) => [...matchKeys.all, id] as const,
  live: () => [...matchKeys.all, "live"] as const,
  upcoming: () => [...matchKeys.all, "upcoming"] as const,
};

export function useMatchesAdmin(filters?: MatchFilters) {
  return useQuery({
    queryKey: matchKeys.list(filters),
    queryFn: () => matchesService.getAllMatches(filters),
    refetchInterval: 45000,
  });
}

export function useMatch(id: number) {
  return useQuery({
    queryKey: matchKeys.detail(id),
    queryFn: () => matchesService.getMatch(id),
    enabled: !!id,
    refetchInterval: 15000,
  });
}

export function useLiveMatches() {
  return useQuery({
    queryKey: matchKeys.live(),
    queryFn: matchesService.getLiveMatches,
    refetchInterval: 30000, // refetch every 30s
  });
}

export function useCreateMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMatchRequest) => matchesService.createMatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchKeys.lists() });
    },
  });
}

export function useUpdateMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMatchRequest }) =>
      matchesService.updateMatch(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchKeys.lists() });
    },
  });
}

export function useDeleteMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => matchesService.deleteMatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: matchKeys.lists() });
    },
  });
}

export function useUpdateMatchStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: MatchStatus }) =>
      matchesService.updateStatus(id, status),
    onMutate: async ({ id, status }) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: matchKeys.lists() });

      // Snapshot current data
      const previousData = queryClient.getQueriesData({ queryKey: matchKeys.lists() });

      // Optimistically update the match status in all list caches
      queryClient.setQueriesData(
        { queryKey: matchKeys.lists() },
        (old: { matches: Match[] } | undefined) => {
          if (!old?.matches) return old;
          return {
            ...old,
            matches: old.matches.map((m: Match) =>
              m.id === id ? { ...m, status } : m
            ),
          };
        }
      );

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      // Rollback on error
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      // Always refetch after to ensure consistency
      queryClient.invalidateQueries({ queryKey: matchKeys.lists() });
      queryClient.invalidateQueries({ queryKey: matchKeys.live() });
    },
  });
}
